import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';

const getAdminSmsBaseEndpoint = (orgName: string) => {
  return `v1/text_to_911/admin-sms/psap/${orgName}`;
};

const AdminSmsSessionResponseSchema = z.object({
  conversation_id: z.string(),
  caller_id: z.string(),
  status: z.enum(['active', 'ended', 'inactive', 'pending', 'timed_out']),
  last_updated_time: z.number(),
});

export type AdminSmsSessionResponse = z.infer<
  typeof AdminSmsSessionResponseSchema
>;

export const adminSmsApi = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches the current admin SMS session status for a caller.
     * @param callerID - phone number (without leading +)
     * @param orgName - Name of authority the user belongs to
     */
    getAdminSmsConversationMetadata: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({
        callerID,
        orgName,
      }: {
        callerID: string;
        orgName: string;
      }) => ({
        ...baseQueryArgs,
        url: `${getAdminSmsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        AdminSmsSessionResponseSchema.parse(response),
      providesTags: (_result, _error, { callerID }) => [
        { type: 'AdminSmsMetaData' as const, id: callerID },
      ],
      transformErrorResponse: (error: FetchBaseQueryError) =>
        typeof error.status === 'number' && error.status === 404
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        maxRetries: 0,
      },
    }),
    /**
     * Ends an admin SMS session with the given phone number. Optionally
     * includes an already-interpolated end_message.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     * @param endMessage - Optional pre-interpolated ending message body
     */
    endAdminSmsConversation: builder.mutation({
      query: ({
        callerID,
        orgName,
        endMessage,
      }: {
        callerID: string;
        orgName: string;
        endMessage?: string;
      }) => ({
        ...baseQueryArgs,
        url: `${getAdminSmsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'PUT',
        body: {
          ...(endMessage !== undefined ? { end_message: endMessage } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'AdminSmsMetaData' as const, id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
});

export const {
  useGetAdminSmsConversationMetadataQuery,
  useEndAdminSmsConversationMutation,
} = adminSmsApi;

/**
 * Action creator that invalidates the cached admin SMS session metadata for a
 * given caller, forcing the active ``getAdminSmsConversationMetadata`` query
 * to re-fetch. Returned action must be dispatched.
 */
export function invalidateAdminSmsConversationMetadata(callerID: string) {
  return adminSmsApi.util.invalidateTags([
    { type: 'AdminSmsMetaData', id: callerID },
  ]);
}
